import { Button, SelectOptionType, Table, addToast } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../../../locales/translations';
import { savePetitionPricesRequest } from '../api';
import usePriceColumns from '../hooks/usePriceColumns';
import { usePricingLoader } from '../Loader';
import { PetitionPaymentTypeId, PetitionType } from '../types';

export default function PetitionsSection() {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_FIANANTIAL_OTHERS,
  );
  const { data } = usePricingLoader();

  const [rows, setRows] = useState<PetitionType[]>(data?.petitionTypes ?? []);
  // La celda del checkbox es no controlada (defaultChecked): remontar la tabla
  // es lo que la deja en sincronía con el estado al marcar/desmarcar.
  const [tableKey, setTableKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const paymentTypeOptions = useMemo<SelectOptionType[]>(
    () =>
      data?.petitionPaymentTypes?.map((paymentType) => ({
        label: paymentType.name,
        value: paymentType.id,
      })) ?? [],
    [data?.petitionPaymentTypes],
  );

  const columns = usePriceColumns({
    showPaymentType: true,
    paymentTypeOptions,
    translateKey: 'petitionTypes',
    onChangeValue: (typeId, fieldName, fieldValue) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.id !== typeId) {
            return row;
          }
          if (fieldName === 'paymentAmount') {
            return { ...row, paymentAmount: fieldValue as number };
          }
          return {
            ...row,
            [fieldName]: fieldValue,
            // El valor sólo aplica al pago fijo: por crédito lo calcula el
            // arancel, así que se limpia al cambiar de tipo.
            paymentAmount:
              fieldValue === PetitionPaymentTypeId.BY_FIXED
                ? row.paymentAmount
                : undefined,
          };
        }),
      );
    },
    onCheck: (typeId, paymentRequired) => {
      setTableKey((prev) => prev + 1);
      setRows((prev) =>
        prev.map((row) =>
          row.id === typeId
            ? {
                ...row,
                paymentRequired,
                paymentAmount: paymentRequired ? row.paymentAmount : undefined,
                paymentType: paymentRequired ? row.paymentType : undefined,
              }
            : row,
        ),
      );
    },
  });

  const onSave = useCallback(async () => {
    setSaving(true);
    const { error } = await savePetitionPricesRequest(rows);
    setSaving(false);
    addToast(
      error
        ? { icon: 'error', color: 'danger', text: t('onSaveError') }
        : { icon: 'success', color: 'success', text: t('onSaveSuccess') },
    );
  }, [rows, t]);

  return (
    <div>
      <p className="fs-16 fw-600 text-primary mb-0">
        {t('petitionTypes.title')}
      </p>
      <p className="fs-14 mb-3">{t('petitionTypes.description')}</p>
      <Table key={tableKey} columns={columns} data={rows} />
      <div className="d-flex justify-content-end mt-3">
        <Button
          text={t('save')}
          size="md"
          disabled={saving}
          loading={saving}
          onClick={onSave}
        />
      </div>
    </div>
  );
}
